<template>
  <component
    v-if="show"
    :is="component"
    :class="{
      'header-link': true,
      [`header-link--${id}`]: true,
      'header-link--active': active,
      'header-link--main': isMain,
      'header-link--cta': isCta,
      'js-open-signup-popup': openSignupPopup,
      'js-open-login-popup': loginPopup && hasJsPopupClass
    }"
    :href="computedHref"
    v-bind="dataAttributes"
    @click="onClick"
  >
    <span v-html="linkCopy" />
    <sprite v-if="menuSprite" class="header-link__menu-sprite" id="app/arrow-down-small" />
  </component>
</template>

<script>
import { mapGetters } from "vuex"
import events from "@/bl-events"
import globals from "@/utils/globals"
import Button from "@/shared/button"
import Sprite from "@/shared/sprite"
import "/wds/sprites/app/arrow-down-small.svg"

export default {
  name: "HeaderLink",
  components: {
    Sprite
  },
  hrefs: {
    login: globals.routes.login,
    signup: globals.routes.signup,
    help: globals.routes.help,
    settings: globals.routes.settings,
    wishlist: globals.routes.wishlist,
    logout: globals.routes.logout,
    pricing: globals.routes.plans,
    discover: globals.routes.forYou,
    highlights: globals.routes.highlights,
    library: globals.routes.library,
    upgrade: globals.routes.plans,
    business: `${globals.routes.business}?utm_source=homepage&utm_medium=link&utm_campaign=header`,
    coaching: `${globals.routes.coaching}?utm_source=homepage&utm_medium=link&utm_campaign=header`
  },
  props: {
    id: { type: String, default: null },
    onlyLogin: { type: Boolean, default: false },
    onlyLogout: { type: Boolean, default: false },
    onlyPremium: { type: Boolean, default: false },
    onlyBasic: { type: Boolean, default: false },
    menuSprite: { type: Boolean, default: false },
    active: { type: Boolean, default: false },
    isMain: { type: Boolean, default: false },
    useRouter: { type: Boolean, default: false },
    hasJsPopupClass: { type: Boolean, default: true },
    href: { type: String, default: null },
    data: { type: Object, default: () => {} }
  },
  computed: {
    ...mapGetters(["isUserLoggedIn", "isUserLoggedOut", "isUserPremium", "isUserBasic"]),
    ...mapGetters("layout", ["startTrialButtonCtaHref"]),
    openSignupPopup() {
      if (this.startTrialButtonCtaHref) {
        return false
      }

      return this.signupPopup && this.hasJsPopupClass
    },
    show() {
      if (this.onlyLogin && !this.isUserLoggedIn) {
        return false
      }
      if (this.onlyLogout && !this.isUserLoggedOut) {
        return false
      }
      if (this.onlyPremium && !this.isUserPremium) {
        return false
      }
      if (this.onlyBasic && !this.isUserBasic) {
        return false
      }
      return true
    },
    isCta() {
      return ["signup", "upgrade"].includes(this.id)
    },
    component() {
      if (this.isCta) {
        return Button
      }
      if (this.computedHref) {
        return "a"
      }
      return "button"
    },
    eventName() {
      return `header-20200608-${this.id.replace(/-/g, "_")}_link`
    },
    loginPopup() {
      return ["login"].includes(this.id)
    },
    signupPopup() {
      return ["signup"].includes(this.id)
    },
    linkCopy() {
      return this.$t(this.id)
    },
    computedHref() {
      if (this.id === "signup" && this.startTrialButtonCtaHref) {
        return this.startTrialButtonCtaHref
      }

      return this.href || this.$options.hrefs[this.id]
    },
    dataAttributes() {
      if (!this.data) {
        return {}
      }
      return Object.entries(this.data).reduce((object, value) => {
        object[`data-${value[0]}`] = value[1]
        return object
      }, {})
    }
  },
  methods: {
    onClick(e) {
      this.$emit("click", e)
      events.track(this.eventName, "1-click")
    }
  },
  i18n: {
    messages: {
      en: {
        "login": "Log in",
        "signup": "Start free trial",
        "signupVariation": "Try it free",
        "pricing": "Pricing",
        "user-menu": "Account",
        "help": "Help&nbsp;&&nbsp;Support",
        "logout": "Logout",
        "settings": "Settings",
        "wishlist": "Wishlist",
        "library": "My Library",
        "discover": "Explore",
        "highlights": "Highlights",
        "upgrade": "Upgrade now",
        "business": "For business",
        "coaching": "Coaching"
      },
      de: {
        "login": "Einloggen",
        "signup": "Jetzt testen",
        "signupVariation": "Jetzt testen",
        "pricing": "Preise",
        "user-menu": "Du",
        "help": "FAQ&nbsp;&&nbsp;Hilfe",
        "logout": "Abmelden",
        "settings": "Einstellungen",
        "wishlist": "Wunschliste",
        "library": "Meine Titel",
        "discover": "Entdecken",
        "highlights": "Markierungen",
        "upgrade": "Jetzt upgraden",
        "business": "Blinkist Business",
        "coaching": "Coaching"
      },
      es: {
        "login": "Iniciar sesión",
        "signup": "Prueba gratis",
        "signupVariation": "Prueba gratis",
        "pricing": "Precios",
        "user-menu": "Cuenta",
        "help": "Ayuda",
        "logout": "Cerrar sesión",
        "settings": "Ajustes",
        "wishlist": "Lista de deseos",
        "library": "Mi biblioteca",
        "discover": "Explorar",
        "highlights": "Destacados",
        "upgrade": "Mejorar tu plan",
        "business": "Para empresas",
        "coaching": "Coaching"
      },
      pt: {
        "login": "Entrar",
        "signup": "Comece o teste gratuito",
        "signupVariation": "Comece o teste gratuito",
        "pricing": "Preços",
        "user-menu": "Conta",
        "help": "Ajuda&nbsp;&&nbsp;Suporte",
        "logout": "Sair",
        "settings": "Configurações",
        "wishlist": "Lista de desejos",
        "library": "Minha biblioteca",
        "discover": "Explorar",
        "highlights": "Destaques",
        "upgrade": "Atualizar agora",
        "business": "Para negócios",
        "coaching": "Coaching"
      }
    }
  }
}
</script>

<style lang="scss">
.header-link {
  $root: &;

  display: flex;
  align-items: center;

  cursor: pointer;

  &:not(#{$root}--cta) {
    @include font-style(button, $color: $midnight);

    &:hover {
      color: $blue;
    }

    &#{$root} {
      &--active {
        color: $green;

        #{$root}__menu-sprite {
          transform: scaleY(-1);
        }
      }

      &--main {
        position: relative;

        height: $spacing-24;
      }

      &--main#{&}--active,
      &--main:hover {
        color: $blue;
      }
    }
  }

  &__menu-sprite {
    @include ease(transform, 0.15s);
  }
}

@media (min-width: 768px) {
  .user-menu__list {
    width: 190px;
  }
}
</style>
